import React, { useState, useEffect, useRef, useCallback } from "react";
import privateAxios from "../../../../utils/axiosPrivate";
import { FaPencilAlt, FaCheck, FaExclamationTriangle } from "react-icons/fa";
import { useParams } from "react-router-dom";
import { EMAIL_REGEX } from "../../../../utils/constants/appConstants";
import {
  customerTab,
  Inputfieldvalidation,
  referencePromoCode
} from "../../../../locales/en";
import { useSelector, useDispatch } from "react-redux";
import { getUsersClientDetailsData } from "../../../../redux/userManagementSlice";
import {
  getFetchCountryCodeURL,
  getUserManagementInfoURL
} from "../../../../utils/constants/apiEndPoints";
import styles from "./../../../../assets/scss/custom.module.scss";
import { sanitizeInput } from "../../../../utils/utils";
import { useToast } from "../../../../hooks/useToast";
import { Tooltip as ReactTooltip } from "react-tooltip";

const UsercontactDetails = () => {
  const toast = useToast();
  const { id } = useParams();
  const [isWebtFocused, setIsWebtFocused] = useState(false);
  const [isbusinessContactNumber, setIsbusinessContactNumber] = useState(false);
  const initialFormDataRef = useRef({});
  const dispatch = useDispatch();
  const { userManagmentClientsData } = useSelector(
    (state) => state.userManagement
  );

  const initialFormData = {
    businessEmail: "",
    businessContactNumber: "",
    registeredAddress: "",
    serviceAddress: "",
    businessWebsite: ""
  };
  const [formData, setFormData] = useState(initialFormData);

  const [errors, setErrors] = useState({});

  const [contactDetailsError, setContactDetailsError] = useState(null);

  const fetchvalidateNumber = useCallback(async (phoneNo, countryCode) => {
    try {
      const response = await privateAxios.get(getFetchCountryCodeURL, {
        params: { number: phoneNo, countryCode }
      });

      if (response.status === 200) {
        setIsbusinessContactNumber(true);
        return true;
      }
      return false;
    } catch (error) {
      setIsbusinessContactNumber(false);
      if (error.response && error.response.status === 400) {
        toast.error(error?.response?.data?.message);
      }
      return false;
    }
  }, []);

  useEffect(() => {
    const businessDetails =
      userManagmentClientsData?.customerDetails?.businessDetails;

    if (!businessDetails) return;

    const { registeredAddress, serviceAddress } = businessDetails;

    const newFormData = {
      businessEmail: businessDetails.businessEmail || "",
      businessContactNumber: businessDetails.businessContactNumber || "",
      registeredAddress:
        registeredAddress?.fullAddress || registeredAddress || "",
      serviceAddress: serviceAddress?.fullAddress || serviceAddress || "",
      businessWebsite: businessDetails.businessWebsite || ""
    };

    setFormData(newFormData);
    initialFormDataRef.current = newFormData;
  }, [userManagmentClientsData]);

  const handleContactDetailsError = (name, value) => {
    const emojiRegex = /\p{Extended_Pictographic}/u;
    if (emojiRegex.test(value)) {
      setContactDetailsError((prev) => ({
        ...prev,
        [name]: Inputfieldvalidation.errorMsg.emojiNotAllowed
      }));
      return false;
    }
    if (name === "businessEmail" && value?.length >= 100) {
      setContactDetailsError((prev) => ({
        ...prev,
        businessEmail: Inputfieldvalidation.errorMsg.emailMaxLength
      }));
      return false;
    } else {
      setContactDetailsError((prev) => ({
        ...prev,
        businessEmail: name === "businessEmail" ? null : prev?.businessEmail
      }));
      return true;
    }
  };

  const handleInputChange = async (event) => {
    const { name, value } = event.target;
    const isValid = handleContactDetailsError(name, value);
    if (!isValid) return;
    let newValue = value;
    if (name === "businessContactNumber") {
      if (newValue === "+") {
        newValue = "";
      } else if (newValue && !newValue.startsWith("+")) {
        newValue = `+${newValue}`;
      }
      if (!/^\+?[0-9]*$/.test(newValue)) {
        return;
      }
    }

    // Update the form data
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: newValue
    }));
  };

  const handleInputBlur = async (event) => {
    const { name, value } = event.target;
    const sanitizedValue = sanitizeInput(value);

    if (formData[name] !== initialFormDataRef.current[name]) {
      if (name === "businessContactNumber" && sanitizedValue) {
        const regex = /^\+?[0-9]*$/;
        if (!regex.test(sanitizedValue)) {
          toast.error(customerTab?.userContactDtls?.errMsg?.item1);
          return;
        }

        const code = sanitizedValue.slice(1, 3);
        const phoneNo = sanitizedValue.slice(3);
        let countryCode = null;

        if (code === "61") {
          countryCode = "au";
        } else if (code === "64") {
          countryCode = "nz";
        } else {
          countryCode = "au";
        }

        if (countryCode) {
          const isValidNumber = await fetchvalidateNumber(phoneNo, countryCode);
          if (!isValidNumber) {
            return;
          }
        }
      }

      if (name === "businessEmail" && sanitizedValue) {
        const isValidEmail = EMAIL_REGEX.test(sanitizedValue);
        if (!isValidEmail) {
          setErrors((prevErrors) => ({
            ...prevErrors,
            businessEmail: referencePromoCode?.errMsg.item5
          }));
          return;
        } else {
          setErrors((prevErrors) => ({
            ...prevErrors,
            businessEmail: ""
          }));
        }
      }

      try {
        const dataTosend = {
          businessDetails: {
            businessEmail: sanitizeInput(formData.businessEmail),
            businessContactNumber: sanitizeInput(
              formData.businessContactNumber
            ),
            registeredAddress: sanitizeInput(formData.registeredAddress),
            serviceAddress: sanitizeInput(formData.serviceAddress),
            businessWebsite: sanitizeInput(formData.businessWebsite)
          }
        };

        const response = await privateAxios.put(
          getUserManagementInfoURL(id),
          dataTosend
        );
        toast.success(response?.data?.message);
        if (response.status === 200) {
          dispatch(getUsersClientDetailsData({ id }));
        }
        if (name === "businessWebsite") {
          setIsWebtFocused(false);
        }
      } catch (error) {
        toast.error(error?.response?.data?.message);
      }
    }
  };

  const handleWebInputFocus = () => {
    setIsWebtFocused(true);
  };

  const handleWebInputBlur = () => {
    setIsWebtFocused(false);
  };

  // No icon until a number is entered, then tick or warning by validity
  const renderValidationIcon = (value, isValid) => {
    if (!value || value.length === 0) {
      return null;
    }
    return isValid ? <FaCheck /> : <FaExclamationTriangle />;
  };

  return (
    <>
      <div className={styles.titleArea}>
        <h5>{customerTab?.userContactDtls.title}</h5>
      </div>
      <div className="row">
        <div className="col-12 col-md-6 col-lg-8 mb-3">
          <div className="blog-input-wrap">
            <div className={`${styles.labelTitle}`}>
              <h5>{customerTab?.userContactDtls?.businessEmail}</h5>
            </div>
            <div className="inputtextfield">
              <input
                type="text"
                className={styles.textfield}
                name="businessEmail"
                value={formData.businessEmail ?? ""}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
              />
              {errors?.businessEmail && (
                <p className="error-message text-danger">
                  <small>{errors?.businessEmail}</small>
                </p>
              )}
              {contactDetailsError?.businessEmail && (
                <p className="error-message text-danger">
                  <small>{contactDetailsError?.businessEmail}</small>
                </p>
              )}
            </div>
          </div>
        </div>
        <div className="col-12 col-md-6 col-lg-4 mb-3">
          <div className="blog-input-wrap">
            <div className={`${styles.labelTitle}`}>
              <h5>
                {renderValidationIcon(
                  formData.businessContactNumber,
                  isbusinessContactNumber
                )}
                {customerTab?.userContactDtls.businessPhone}
              </h5>
            </div>
            <div className="inputtextfield">
              <input
                type="text"
                className={styles.textfield}
                name="businessContactNumber"
                maxLength="12"
                value={formData.businessContactNumber ?? ""}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
              />
            </div>
          </div>
        </div>
        <div className="col-12 mb-3">
          <div className="blog-input-wrap">
            <div className={`${styles.labelTitle}`}>
              <h5>{customerTab?.userContactDtls.registerdAdress}</h5>
            </div>
            <div className="inputtextfield">
              <input
                type="text"
                className={styles.textfield}
                name="registeredAddress"
                value={
                  typeof formData.registeredAddress === "object"
                    ? (formData.registeredAddress?.fullAddress ?? "")
                    : (formData?.registeredAddress ?? "")
                }
                onChange={handleInputChange}
                onBlur={handleInputBlur}
              />
            </div>
          </div>
        </div>
        <div className="col-12 mb-3">
          <div className="blog-input-wrap">
            <div className={`${styles.labelTitle}`}>
              <h5>{customerTab?.userContactDtls.addressForService}</h5>
            </div>
            <div className="inputtextfield">
              <input
                type="text"
                className={styles.textfield}
                name="serviceAddress"
                value={
                  typeof formData.serviceAddress === "object"
                    ? (formData.serviceAddress?.fullAddress ?? "")
                    : (formData.serviceAddress ?? "")
                }
                onChange={handleInputChange}
                onBlur={handleInputBlur}
              />
            </div>
          </div>
        </div>
        <div className="col-12 mb-3">
          <div className="blog-input-wrap">
            <div className={`${styles.labelTitle}`}>
              <h5>{customerTab?.userContactDtls.website}</h5>
            </div>
            <div className="inputtextfield">
              {(formData.businessWebsite === "" || isWebtFocused) && (
                <input
                  type="text"
                  name="businessWebsite"
                  value={formData.businessWebsite ?? ""}
                  onChange={handleInputChange}
                  onFocus={handleWebInputFocus}
                  onBlur={(e) => {
                    handleInputBlur(e);
                    handleWebInputBlur();
                  }}
                  className={styles.textfield}
                />
              )}
              {formData.businessWebsite !== "" && !isWebtFocused && (
                <div className="weblinkname d-flex align-items-center">
                  {formData.businessWebsite ? (
                    <a
                      href={
                        formData.businessWebsite.startsWith("http")
                          ? formData.businessWebsite
                          : `https://${formData.businessWebsite}`
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {formData.businessWebsite}
                    </a>
                  ) : (
                    <span>{customerTab?.userContactDtls.noWebsite}</span>
                  )}
                  <ul
                    className={`${styles.btngroupwrap} ms-1 d-flex align-items-center list-no-padding`}
                  >
                    <li>
                      <button
                        className={`${styles.btnPrimary} added-icon ms-1`}
                        onClick={handleWebInputFocus}
                        data-tooltip-id="edit-tooltip"
                        data-tooltip-content="Edit"
                      >
                        <FaPencilAlt />
                      </button>
                    </li>
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <ReactTooltip id="edit-tooltip" place="top" className="tooltipcustom" />
    </>
  );
};

export default UsercontactDetails;
